/** Exec-pricing escape audit (CONF-106 part 2 amendment, spec §2-3).
 *  buildEscapeEvent: pure payload assembly from state App.js already holds.
 *  recordEscapeEvent: FAIL-CLOSED (unlike recordAuditEvent) — an unlogged
 *  escape is the hole this module closes, so callers abort when it returns
 *  false. Money: component pairs integer µ$; price pairs dollars (engine
 *  output); totals deltas computed in integer cents. */
import { generateClient } from 'aws-amplify/api';
import { createAuditEvent } from '../graphql/mutations';
import { getProductPrice } from './configUtilities';
import { priceSku } from '../pricing/engine';

const toCents = (dollars) => Math.round(dollars * 100);

/** Sums BOM quantity by part number. Was duplicated verbatim in
 *  buildEscapeEvent and buildPersistEvents. */
function qtyByPnOf(bom) {
  const qtyByPn = {};
  for (const row of bom || []) if (row.partNumber) qtyByPn[row.partNumber] = (qtyByPn[row.partNumber] ?? 0) + (row.quantity ?? 0);
  return qtyByPn;
}

/** Actor identity for audit payloads, tolerant of both auth shapes: the
 *  primary login path builds `user` from Amplify v6 getCurrentUser(), which
 *  carries `userId` (never `attributes`); legacy/v5-shaped users carry
 *  `attributes.sub`. Without the userId fallback, actorSub is null on every
 *  normal login (final-review I1). */
export const actorFromUser = (user) => ({
  email: user?.email ?? user?.attributes?.email ?? null,
  sub: user?.userId ?? user?.attributes?.sub ?? null,
});

export function buildEscapeEvent(artifactType, ctx) {
  const { execPricing, config, pricingData, productDb, bom,
          quoteSerial, customerName, quoteName, actor, vendorId, appVersion } = ctx;
  const { overrides, snapshot, customPrices, overrideSetId } = execPricing;

  const componentChanges = [];
  for (const [productSku, comps] of Object.entries(overrides.componentCosts)) {
    for (const [componentSku, overrideMicros] of Object.entries(comps)) {
      componentChanges.push({
        productSku, componentSku,
        catalogMicros: snapshot?.componentCostMicros?.[componentSku] ?? null,
        overrideMicros,
      });
    }
  }

  const qtyByPn = qtyByPnOf(bom);

  const priceChanges = Object.entries(customPrices).map(([partNumber, customPrice]) => {
    const inProductDb = !!productDb?.products?.[partNumber];
    const catalogPrice = inProductDb
      ? getProductPrice(productDb, config.quarter, partNumber)
      : priceSku(partNumber, snapshot).discountCents / 100;
    return { partNumber, qty: qtyByPn[partNumber] ?? 0, catalogPrice, customPrice };
  });

  const deltaCents = priceChanges.reduce(
    (sum, c) => sum + (toCents(c.customPrice) - toCents(c.catalogPrice)) * c.qty, 0);
  const customSolutionTotal = pricingData?.solution?.grandTotal ?? null;
  const catalogSolutionTotal = customSolutionTotal != null
    ? (toCents(customSolutionTotal) - deltaCents) / 100 : null;
  const deltaPct = catalogSolutionTotal
    ? Math.round((deltaCents / toCents(catalogSolutionTotal)) * 10000) / 100 : null;

  return {
    eventType: 'execPricingEscape',
    artifactType,
    quoteSerial: quoteSerial ?? null,
    vendor: vendorId,
    payload: {
      actorEmail: actor?.email ?? null, actorSub: actor?.sub ?? null,
      overrideSetId: overrideSetId ?? null,
      vendor: vendorId, quarter: config?.quarter ?? null,
      customerName: customerName ?? null, quoteName: quoteName ?? null,
      componentChanges,
      integrationPct: overrides.integrationBp !== null
        ? { catalogBp: snapshot?.integrationBp ?? null, overrideBp: overrides.integrationBp }
        : null,
      priceChanges,
      totals: { catalogSolutionTotal, customSolutionTotal,
                deltaDollars: deltaCents / 100, deltaPct },
      appVersion: appVersion ?? null,
    },
  };
}

export async function recordEscapeEvent(event) {
  try {
    const client = generateClient();
    const input = { ...event, payload: JSON.stringify(event.payload) };
    const res = await client.graphql({ query: createAuditEvent, variables: { input } });
    return !!res?.data?.createAuditEvent;
  } catch (error) {
    console.error('Exec pricing escape audit write FAILED:', error);
    return false;
  }
}

/** Per-line persist events (part 3, spec §2.4) — the Epic C threshold seam.
 *  One AuditEvent per custom-priced line, written when overrides are PERSISTED
 *  (quote save) or a reprice is accepted. Same catalog-vs-custom pairing as
 *  buildEscapeEvent's priceChanges, exploded to line grain. */
export function buildPersistEvents(artifactType, ctx) {
  const { execPricing, config, productDb, bom, quoteSerial, actor, vendorId, appVersion } = ctx;
  const { snapshot, customPrices, overrideSetId } = execPricing;

  const qtyByPn = qtyByPnOf(bom);

  // Spec §2.4: one event per affected QUOTE line. customPrices can cover the
  // entire catalog (an integration-% override reprices every composition SKU),
  // but SKUs the BOM doesn't carry are not quote lines — emitting them would
  // explode a save into 100+ serial writes (final-review I2).
  return Object.entries(customPrices)
    .filter(([partNumber]) => (qtyByPn[partNumber] ?? 0) > 0)
    .map(([partNumber, customPrice]) => {
    const inProductDb = !!productDb?.products?.[partNumber];
    const catalogPrice = inProductDb
      ? getProductPrice(productDb, config.quarter, partNumber)
      : priceSku(partNumber, snapshot).discountCents / 100;
    const deltaCents = toCents(customPrice) - toCents(catalogPrice);
    return {
      eventType: 'execPricingPersist',
      artifactType,
      quoteSerial: quoteSerial ?? null,
      vendor: vendorId,
      payload: {
        partNumber,
        qty: qtyByPn[partNumber],
        catalogPrice,
        customPrice,
        deltaDollars: deltaCents / 100,
        deltaPct: toCents(catalogPrice) !== 0
          ? Math.round((deltaCents / toCents(catalogPrice)) * 10000) / 100 : null,
        overrideSetId: overrideSetId ?? null,
        quarter: config?.quarter ?? null,
        actorEmail: actor?.email ?? null,
        actorSub: actor?.sub ?? null,
        appVersion: appVersion ?? null,
      },
    };
  });
}

/** Sequential + fail-closed: the caller aborts the persist action on false.
 *  Rows written before a failure reference a quoteSerial that never saved —
 *  harmless orphans (spec §2.1). */
export async function recordPersistEvents(events) {
  for (const event of events) {
    if (!(await recordEscapeEvent(event))) return false;
  }
  return true;
}
